import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import React from 'react';

import TrashIcon from '@/assets/icons/trash.svg?react';
import { ItemTitle } from '@/components/molecules/ItemTitle';
import { Field, FieldType } from '@/types/fields';
import { Identified } from '@/types/identified';

interface Props {
  readonly field: Field<any>;
  readonly item: Identified<any>;
  readonly counter: number;
  readonly title?: string;

  onChange(item: Identified<any>): void;
  onRemove(item: Identified<any>): void;
}

export const SingleFieldListFormFieldItem: React.FC<
  React.PropsWithoutRef<Props> & React.RefAttributes<HTMLDivElement>
> = React.forwardRef(function SingleFieldListFormFieldItem(
  props: Props,
  ref: React.Ref<HTMLDivElement>,
): React.ReactElement {
  const { counter, field, item, title, onChange, onRemove } = props;

  const span = React.useMemo((): number => field.columnSpan ?? 1, [field.columnSpan]);
  const gridColumn = React.useMemo((): string => `auto / span ${span}`, [span]);
  const name = React.useMemo((): string => String(field.name), [field.name]);
  const value = React.useMemo((): unknown => {
    return item[name as keyof typeof item] ?? '';
  }, [item, name]); /// \_o_/ yeah!!! right?

  if (field.type !== FieldType.text) {
    throw new Error('single child ListField expects field type to be text');
  } else if (typeof value !== 'string') {
    throw new Error('single child ListField expects value to be a string');
  }

  const onSingleChangeWrapper = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement>): void => {
      const { value } = event.target;
      // Ignore empty value
      if (value.trim() === '') {
        return;
      }

      onChange({ ...item, [field.name]: value.trim() });
    },
    [field, item, onChange],
  );

  const onRemoveWrapper = React.useCallback((): void => {
    onRemove(item);
  }, [item, onRemove]);

  const InputProps = {
    endAdornment:
      item.id === '' ? null : (
        <InputAdornment sx={adornmentSx} position="end">
          <IconButton
            role="button"
            color="inherit"
            aria-label="Remove item"
            onClick={onRemoveWrapper}>
            <TrashIcon fill="currentColor" />
          </IconButton>
        </InputAdornment>
      ),
  };

  return (
    <React.Fragment>
      <ItemTitle title={title} counter={counter} />
      <Box ref={ref} gridColumn={gridColumn} minWidth={0}>
        <TextField
          defaultValue={value}
          label={field.label}
          fullWidth={true}
          InputProps={InputProps}
          onBlur={onSingleChangeWrapper}
        />
      </Box>
    </React.Fragment>
  );
});

// TODO: this is hard, not sure why should we need it
const adornmentSx = {
  position: 'absolute',
  right: 24,
  color: 'text.primary',
};
